import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Play, VolumeX, Award } from "lucide-react";

const feedbackCards = [
  {
    id: 1,
    videoId: "6BLuHegBaf0",
    client: "Vanitaa Pande",
    title: "Aakaar Wellness & Billabong high International School, Bhopal",
    subtitle: "High-End Styling & Narrative Production",
    desc: "Orchestrating luxury narrative visual layouts, cinematic color grading, and viral social campaigns that reached millions globally.",
    metric: "2.4M+ Views",
    tag: "CINEMATIC MEDIA"
  },
  {
    id: 2,
    videoId: "MPnwGumdKVo",
    client: "Laxminarayan Fabrics",
    title: "A Legacy Reborn in the Digital Era",
    subtitle: "Brand & E-Commerce Transformation",
    desc: "How we took a legacy textile brand and engineered a high-speed React storefront, doubling digital orders within the first quarter.",
    metric: "3.5x Orders",
    tag: "TECH & APPAREL"
  },
  {
    id: 3,
    videoId: "ORUWmbu58RU",
    client: "Tesla Transformers & Nakhrali Limited Edition",
    title: "Cinematic Narrative & Festival Scale",
    subtitle: "Film Production & Sound Engineering",
    desc: "Collaborating on flagship narrative features, navigating high-end color grading, sound engineering, and global festival distribution.",
    metric: "6 Nominations",
    tag: "FILM-MAKING"
  },
  {
    id: 4,
    videoId: "cUaRAYsC5q4",
    client: "Kairos Labs",
    title: "Interactive Identity & Kinetic Scale",
    subtitle: "Web UI/UX & Kinetic Motion Design",
    desc: "Fusing clean React frontend layouts with modern micro-animations, creating a cohesive, immersive brand presence for Series-A pitching.",
    metric: "Series-A Launch",
    tag: "UI/UX DESIGN"
  }
];

const ClientFeedbackShowcase = () => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: targetRef });

  // Custom transform range for 4 items: starts at 2%, ends at -72%
  const x = useTransform(scrollYProgress, [0, 1], ["2%", "-72%"]);

  return (
    <section
      id="feedback"
      ref={targetRef}
      className="relative"
      style={{ height: "240vh", background: "#0a0908" }}
    >
      <div className="sticky top-0 h-[100dvh] flex flex-col overflow-hidden border-b border-[var(--rule)]">
        {/* Header */}
        <div className="container-xl pt-24 pb-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-4">
                <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">06.5</span>
                <span className="w-16 h-px bg-[var(--rule)]" />
                <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">SUCCESS DIARIES</span>
              </div>
              <motion.h2
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9 }}
                className="mt-4 font-display text-[clamp(36px,5.5vw,72px)] leading-[1.05] text-[var(--cream)] balance"
              >
                Recent <span className="italic gold-text">chapters</span><br className="hidden md:block" /> from the studio.
              </motion.h2>
            </div>
            <div className="flex items-center gap-3 font-impact text-[10px] tracking-[0.35em] text-[var(--muted)]">
              <span>SCROLL ↓</span>
              <span className="w-10 h-px bg-[var(--rule)]" />
              <span className="text-[var(--gold)] font-impact">VLOGS TRACK</span>
            </div>
          </div>
        </div>

        {/* Horizontal Scroll Area */}
        <div className="flex-1 flex items-center overflow-hidden">
          <motion.div style={{ x }} className="flex gap-6 md:gap-10 pl-8">
            {feedbackCards.map((c, i) => (
              <motion.article
                key={c.id}
                className="relative shrink-0 rounded-3xl overflow-hidden bg-[#121110] border border-white/5 hover:border-[var(--gold)]/40 p-4 md:p-6 flex flex-col aspect-[4/5] md:aspect-[1.5/1]"
                style={{
                  width: "min(90vw, 760px)",
                  backdropFilter: "blur(20px)"
                }}
                whileHover={{ y: -6 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-cursor="play"
              >
                <div className="grid md:grid-cols-[1.2fr_1fr] gap-6 h-full items-center">

                  {/* Left Column: Muted Autoplay Video Embed with Controls Hidden */}
                  <div className="relative aspect-video md:h-full w-full rounded-2xl overflow-hidden border border-[rgba(201,169,97,0.15)] bg-black/80">
                    <iframe
                      className="absolute inset-0 w-full h-full border-0 pointer-events-none"
                      src={`https://www.youtube.com/embed/${c.videoId}?autoplay=1&mute=1&loop=1&playlist=${c.videoId}&controls=0&rel=0&modestbranding=1&iv_load_policy=3&showinfo=0`}
                      title={`${c.client} Feedback Video`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      loading="lazy"
                    ></iframe>

                    {/* Dark Glass Overlay with hover reveal */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />

                    {/* Custom Autoplay & Mute indicator */}
                    <div className="absolute bottom-3 left-3 bg-black/60 backdrop-blur-md border border-white/10 px-3 py-1 rounded-full flex items-center gap-2">
                      <VolumeX size={10} className="text-[var(--gold)] animate-pulse" />
                      <span className="font-mono text-[8px] uppercase tracking-widest text-[var(--cream)]">Autoplay Muted</span>
                    </div>

                    <div className="absolute top-3 right-3 bg-[var(--gold)]/10 backdrop-blur-md border border-[var(--gold)]/30 px-3 py-1 rounded-full flex items-center gap-1.5">
                      <Play size={8} fill="var(--gold)" className="text-[var(--gold)]" />
                      <span className="font-impact text-[8px] uppercase tracking-wider text-[var(--gold)]">Success Story</span>
                    </div>
                  </div>

                  {/* Right Column: Feedback Copy & Meta details */}
                  <div className="flex flex-col justify-between h-full py-2">
                    {/* Index + Tag */}
                    <div className="flex items-center justify-between border-b border-[var(--rule)] pb-4 mb-4">
                      <span className="font-impact text-[10px] tracking-[0.3em] text-[var(--gold)]">
                        CHAPTER {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-impact text-[9px] tracking-[0.25em] bg-white/5 border border-white/10 text-[var(--cream-muted)] px-3 py-1 rounded-full uppercase">
                        {c.tag}
                      </span>
                    </div>

                    {/* Review copy */}
                    <div className="flex-1">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-impact block mb-1">
                        {c.client}
                      </span>
                      <h3 className="font-display text-2xl md:text-3xl text-[var(--cream)] leading-snug group-hover:text-[var(--gold)] transition-colors">
                        {c.title}
                      </h3>
                      <p className="mt-3 text-[12px] md:text-[13px] leading-relaxed text-[var(--cream-muted)] line-clamp-3">
                        {c.desc}
                      </p>
                    </div>

                    {/* Metric / Bottom Row */}
                    <div className="mt-6 pt-4 border-t border-[var(--rule)] flex items-center justify-between">
                      <div>
                        <div className="font-impact text-[8px] md:text-[9px] tracking-[0.3em] text-[var(--gold)] uppercase">
                          PARTNERSHIP IMPACT
                        </div>
                        <div className="font-display text-base md:text-lg text-[var(--cream)] mt-1">
                          {c.metric}
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 text-[var(--gold)]">
                        <span className="text-[9px] uppercase tracking-widest font-impact">Success Vlog</span>
                        <ArrowUpRight size={14} />
                      </div>
                    </div>

                  </div>

                </div>
              </motion.article>
            ))}

            {/* End plate */}
            <div
              className="shrink-0 flex flex-col items-start justify-center px-8 md:px-12"
              style={{ width: "min(85vw, 420px)" }}
            >
              <div className="font-display italic text-[clamp(32px,4.5vw,56px)] leading-[1.05] text-[var(--cream)]">
                your <span className="gold-text">story</span><br />
                starts next.
              </div>
              <a href="/careers" className="btn-primary mt-8 whitespace-nowrap">
                Work with us
                <ArrowUpRight size={16} />
              </a>
            </div>
          </motion.div>
        </div>

        {/* Progress bar */}
        <div className="container-xl pb-8">
          <div className="relative h-px w-full bg-[rgba(201,169,97,0.15)] overflow-hidden">
            <motion.div
              style={{ scaleX: scrollYProgress, transformOrigin: "left" }}
              className="absolute inset-0"
              initial={false}
            >
              <div
                className="h-full w-full"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(228,199,122,0.3), #e4c77a 50%, #b87333)"
                }}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientFeedbackShowcase;
